// src/pages/finance-portal/payroll-employees/PayrollEmployeeForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, MenuItem, Select,
    FormControl, InputLabel, InputAdornment, CircularProgress, Alert,
    Autocomplete, Checkbox, FormControlLabel, IconButton, Chip,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    mb: 3,
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

const sectionTitle = {
    fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b',
};

const FILING_STATUS_OPTIONS = [
    { value: 'SINGLE', label: 'Single' },
    { value: 'MARRIED_FILING_JOINTLY', label: 'Married Filing Jointly' },
    { value: 'MARRIED_FILING_SEPARATELY', label: 'Married Filing Separately' },
    { value: 'HEAD_OF_HOUSEHOLD', label: 'Head of Household' },
];

const PAY_FREQUENCY_OPTIONS = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'BIWEEKLY', label: 'Bi-Weekly' },
    { value: 'SEMIMONTHLY', label: 'Semi-Monthly' },
    { value: 'MONTHLY', label: 'Monthly' },
];

const ACCOUNT_TYPE_OPTIONS = [
    { value: 'CHECKING', label: 'Checking' },
    { value: 'SAVINGS', label: 'Savings' },
];

const emptyBankAccount = {
    account_name: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'CHECKING',
    is_primary: false,
};

const initialForm = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    ssn_last_four: '',
    date_of_birth: '',
    hire_date: new Date().toISOString().split('T')[0],
    worker_type: 'EMPLOYEE',
    compensation_type: 'SALARY',
    compensation_amount: '',
    pay_frequency: 'BIWEEKLY',
    weekly_hours: '40',
    filing_status: 'SINGLE',
    dependents: '0',
    additional_withholding: '',
    other_income: '',
    deductions_amount: '',
    exempt_federal: false,
    exempt_fica: false,
    work_state: '',
    state_filing_status: '',
    state_allowances: '0',
    bank_accounts: [],
    department: '',
    cost_center: '',
    gl_account_id: null,
    status: 'ACTIVE',
};

export default function PayrollEmployeeForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = !!id;

    const [form, setForm] = useState({ ...initialForm });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // GL Accounts
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);

    const fetchGlAccounts = useCallback(async () => {
        setGlLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) {
                setGlAccounts(res.data.data || []);
            }
        } catch {
            // silent
        } finally {
            setGlLoading(false);
        }
    }, []);

    const fetchEmployee = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/payroll/employees/${id}`);
            if (res.data.success) {
                const emp = res.data.data;
                setForm({
                    first_name: emp.first_name || '',
                    last_name: emp.last_name || '',
                    email: emp.email || '',
                    phone: emp.phone || '',
                    ssn_last_four: emp.ssn_last_four || '',
                    date_of_birth: emp.date_of_birth || '',
                    hire_date: emp.hire_date || '',
                    worker_type: emp.worker_type || 'EMPLOYEE',
                    compensation_type: emp.compensation_type || 'SALARY',
                    compensation_amount: emp.compensation_amount ?? '',
                    pay_frequency: emp.pay_frequency || 'BIWEEKLY',
                    weekly_hours: emp.weekly_hours ?? '40',
                    filing_status: emp.filing_status || 'SINGLE',
                    dependents: emp.dependents ?? '0',
                    additional_withholding: emp.additional_withholding ?? '',
                    other_income: emp.other_income ?? '',
                    deductions_amount: emp.deductions_amount ?? '',
                    exempt_federal: emp.exempt_federal || false,
                    exempt_fica: emp.exempt_fica || false,
                    work_state: emp.work_state || '',
                    state_filing_status: emp.state_filing_status || '',
                    state_allowances: emp.state_allowances ?? '0',
                    bank_accounts: Array.isArray(emp.bank_accounts) ? emp.bank_accounts : [],
                    department: emp.department || '',
                    cost_center: emp.cost_center || '',
                    gl_account_id: emp.gl_account_id || null,
                    status: emp.status || 'ACTIVE',
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load employee');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchGlAccounts();
        fetchEmployee();
    }, [fetchGlAccounts, fetchEmployee]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    // Bank accounts
    const addBankAccount = () => {
        setForm(prev => ({
            ...prev,
            bank_accounts: [...prev.bank_accounts, { ...emptyBankAccount }],
        }));
    };

    const updateBankAccount = (index, field, value) => {
        setForm(prev => {
            const accounts = [...prev.bank_accounts];
            accounts[index] = { ...accounts[index], [field]: value };
            if (field === 'is_primary' && value) {
                accounts.forEach((a, i) => {
                    if (i !== index) a.is_primary = false;
                });
            }
            return { ...prev, bank_accounts: accounts };
        });
    };

    const removeBankAccount = (index) => {
        setForm(prev => ({
            ...prev,
            bank_accounts: prev.bank_accounts.filter((_, i) => i !== index),
        }));
    };

    const handleSave = async (asDraft = false) => {
        setSaving(true);
        setError(null);
        try {
            const payload = { ...form };
            if (asDraft) payload.status = 'INACTIVE';

            if (isEdit) {
                await axiosInstance.put(`/api/finance/payroll/employees/${id}`, payload);
                enqueueSnackbar('Employee updated successfully.', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/payroll/employees', payload);
                enqueueSnackbar('Employee created successfully.', { variant: 'success' });
            }
            navigate('/finance-portal/payroll-employees');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save employee');
            enqueueSnackbar(err.response?.data?.message || 'Failed to save employee.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const findGlAccount = (glId) => {
        if (!glId) return null;
        return glAccounts.find(a => a.id === glId) || null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, fontFamily, maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate('/finance-portal/payroll-employees')} sx={{ color: '#64748b' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ p: 1.2, borderRadius: '12px', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PersonIcon sx={{ fontSize: 24, color: ACCENT }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#1e293b' }}>
                        {isEdit ? 'Edit Employee' : 'New Employee'}
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        {isEdit ? 'Update employee details' : 'Add a new employee or contractor'}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

            {/* Basic Info */}
            <Paper elevation={0} sx={cardSx}>
                <Typography sx={sectionTitle}>Basic Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <TextField label="First Name" size="small" value={form.first_name} onChange={e => handleChange('first_name', e.target.value)} sx={inputSx} required />
                    <TextField label="Last Name" size="small" value={form.last_name} onChange={e => handleChange('last_name', e.target.value)} sx={inputSx} required />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <TextField label="Email" size="small" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} sx={inputSx} required />
                    <TextField label="Phone" size="small" value={form.phone} onChange={e => handleChange('phone', e.target.value)} sx={inputSx} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2.5 }}>
                    <TextField label="SSN Last 4" size="small" value={form.ssn_last_four} onChange={e => handleChange('ssn_last_four', e.target.value)} inputProps={{ maxLength: 4 }} placeholder="XXXX" sx={inputSx} />
                    <TextField label="Date of Birth" size="small" type="date" value={form.date_of_birth} onChange={e => handleChange('date_of_birth', e.target.value)} InputLabelProps={{ shrink: true, sx: { fontFamily } }} sx={inputSx} />
                    <TextField label="Hire Date" size="small" type="date" value={form.hire_date} onChange={e => handleChange('hire_date', e.target.value)} InputLabelProps={{ shrink: true, sx: { fontFamily } }} sx={inputSx} required />
                </Box>
            </Paper>

            {/* Worker Type & Compensation */}
            <Paper elevation={0} sx={cardSx}>
                <Typography sx={sectionTitle}>Worker Type & Compensation</Typography>
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 1 }}>Worker Type</Typography>
                    <ToggleButtonGroup
                        value={form.worker_type}
                        exclusive
                        onChange={(_, val) => { if (val) handleChange('worker_type', val); }}
                        sx={{ '& .MuiToggleButton-root': { fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3 } }}
                    >
                        <ToggleButton value="EMPLOYEE" sx={{ '&.Mui-selected': { bgcolor: '#dbeafe', color: '#1e40af' } }}>Employee (W-2)</ToggleButton>
                        <ToggleButton value="CONTRACTOR" sx={{ '&.Mui-selected': { bgcolor: '#e0e7ff', color: '#3730a3' } }}>Contractor (1099)</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <FormControl size="small" sx={inputSx}>
                        <InputLabel sx={{ fontFamily }}>Compensation Type</InputLabel>
                        <Select value={form.compensation_type} label="Compensation Type" onChange={e => handleChange('compensation_type', e.target.value)} sx={{ fontFamily, borderRadius: '10px' }}>
                            <MenuItem value="SALARY" sx={{ fontFamily }}>Salary</MenuItem>
                            <MenuItem value="HOURLY" sx={{ fontFamily }}>Hourly</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={form.compensation_type === 'SALARY' ? 'Annual Salary' : 'Hourly Rate'}
                        size="small"
                        type="number"
                        value={form.compensation_amount}
                        onChange={e => handleChange('compensation_amount', e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={inputSx}
                        required
                    />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                    <FormControl size="small" sx={inputSx}>
                        <InputLabel sx={{ fontFamily }}>Pay Frequency</InputLabel>
                        <Select value={form.pay_frequency} label="Pay Frequency" onChange={e => handleChange('pay_frequency', e.target.value)} sx={{ fontFamily, borderRadius: '10px' }}>
                            {PAY_FREQUENCY_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Weekly Hours"
                        size="small"
                        type="number"
                        value={form.weekly_hours}
                        onChange={e => handleChange('weekly_hours', e.target.value)}
                        InputProps={{ endAdornment: <InputAdornment position="end">hrs</InputAdornment> }}
                        sx={inputSx}
                    />
                </Box>
            </Paper>

            {/* W-4 Tax Withholding (only for Employees) */}
            {form.worker_type === 'EMPLOYEE' && (
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={sectionTitle}>W-4 Tax Withholding</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                        <FormControl size="small" sx={inputSx}>
                            <InputLabel sx={{ fontFamily }}>Filing Status</InputLabel>
                            <Select value={form.filing_status} label="Filing Status" onChange={e => handleChange('filing_status', e.target.value)} sx={{ fontFamily, borderRadius: '10px' }}>
                                {FILING_STATUS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Number of Dependents" size="small" type="number" value={form.dependents} onChange={e => handleChange('dependents', e.target.value)} inputProps={{ min: 0 }} sx={inputSx} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                        <TextField
                            label="Additional Withholding"
                            size="small"
                            type="number"
                            value={form.additional_withholding}
                            onChange={e => handleChange('additional_withholding', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            helperText="Extra per pay period"
                            sx={inputSx}
                        />
                        <TextField
                            label="Other Income"
                            size="small"
                            type="number"
                            value={form.other_income}
                            onChange={e => handleChange('other_income', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            helperText="Step 4(a) amount"
                            sx={inputSx}
                        />
                        <TextField
                            label="Deductions"
                            size="small"
                            type="number"
                            value={form.deductions_amount}
                            onChange={e => handleChange('deductions_amount', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            helperText="Step 4(b) amount"
                            sx={inputSx}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.exempt_federal}
                                    onChange={e => handleChange('exempt_federal', e.target.checked)}
                                    sx={{ '&.Mui-checked': { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontWeight: 500 }}>Exempt from Federal Withholding</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.exempt_fica}
                                    onChange={e => handleChange('exempt_fica', e.target.checked)}
                                    sx={{ '&.Mui-checked': { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontWeight: 500 }}>Exempt from FICA</Typography>}
                        />
                    </Box>
                </Paper>
            )}

            {/* State Tax */}
            {form.worker_type === 'EMPLOYEE' && (
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={sectionTitle}>State Tax</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2.5 }}>
                        <TextField label="Work State" size="small" value={form.work_state} onChange={e => handleChange('work_state', e.target.value)} placeholder="e.g. CA" sx={inputSx} />
                        <TextField label="State Filing Status" size="small" value={form.state_filing_status} onChange={e => handleChange('state_filing_status', e.target.value)} sx={inputSx} />
                        <TextField label="State Allowances" size="small" type="number" value={form.state_allowances} onChange={e => handleChange('state_allowances', e.target.value)} inputProps={{ min: 0 }} sx={inputSx} />
                    </Box>
                </Paper>
            )}

            {/* Bank Accounts */}
            <Paper elevation={0} sx={cardSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Typography sx={sectionTitle}>Bank Accounts</Typography>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addBankAccount}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none',
                            borderRadius: '10px', color: ACCENT,
                            '&:hover': { bgcolor: '#f0fdf4' },
                        }}
                    >
                        Add Account
                    </Button>
                </Box>

                {form.bank_accounts.length === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center', borderRadius: '12px', border: '2px dashed #e2e8f0', mb: 2 }}>
                        <Typography sx={{ fontFamily, color: '#94a3b8', mb: 1 }}>No bank accounts added yet.</Typography>
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>Add a bank account for direct deposit.</Typography>
                    </Box>
                )}

                {form.bank_accounts.map((acct, index) => (
                    <Paper key={index} elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#94a3b8', fontSize: '0.85rem' }}>Account #{index + 1}</Typography>
                                {acct.is_primary && <Chip label="Primary" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: '#dcfce7', color: '#166534' }} />}
                            </Box>
                            <IconButton size="small" onClick={() => removeBankAccount(index)} sx={{ color: '#ef4444' }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField label="Account Name" size="small" value={acct.account_name} onChange={e => updateBankAccount(index, 'account_name', e.target.value)} placeholder="e.g. Personal Checking" sx={inputSx} />
                            <TextField label="Bank Name" size="small" value={acct.bank_name} onChange={e => updateBankAccount(index, 'bank_name', e.target.value)} sx={inputSx} />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                            <TextField label="Routing Number" size="small" value={acct.routing_number} onChange={e => updateBankAccount(index, 'routing_number', e.target.value)} inputProps={{ maxLength: 9 }} sx={inputSx} />
                            <TextField label="Account Number" size="small" value={acct.account_number} onChange={e => updateBankAccount(index, 'account_number', e.target.value)} sx={inputSx} />
                            <FormControl size="small" sx={inputSx}>
                                <InputLabel sx={{ fontFamily }}>Account Type</InputLabel>
                                <Select value={acct.account_type} label="Account Type" onChange={e => updateBankAccount(index, 'account_type', e.target.value)} sx={{ fontFamily, borderRadius: '10px' }}>
                                    {ACCOUNT_TYPE_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={acct.is_primary}
                                        onChange={e => updateBankAccount(index, 'is_primary', e.target.checked)}
                                        sx={{ '&.Mui-checked': { color: ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontWeight: 500, fontSize: '0.85rem' }}>Primary Account</Typography>}
                            />
                        </Box>
                    </Paper>
                ))}
            </Paper>

            {/* Department & GL */}
            <Paper elevation={0} sx={cardSx}>
                <Typography sx={sectionTitle}>Department & GL Mapping</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2.5 }}>
                    <TextField label="Department" size="small" value={form.department} onChange={e => handleChange('department', e.target.value)} sx={inputSx} />
                    <TextField label="Cost Center" size="small" value={form.cost_center} onChange={e => handleChange('cost_center', e.target.value)} sx={inputSx} />
                    <Autocomplete
                        size="small"
                        options={glAccounts}
                        getOptionLabel={opt => opt ? `${opt.account_number || ''} - ${opt.name || opt.account_name || ''}` : ''}
                        value={findGlAccount(form.gl_account_id)}
                        onChange={(_, val) => handleChange('gl_account_id', val ? val.id : null)}
                        loading={glLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="GL Account"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' },
                                    endAdornment: (
                                        <>
                                            {glLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        noOptionsText="No accounts found"
                    />
                </Box>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none',
                        borderRadius: '10px', borderColor: ACCENT, color: ACCENT,
                        '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' }, px: 3,
                    }}
                >
                    Save as Draft
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none',
                        borderRadius: '10px', bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#16a34a' }, px: 4,
                    }}
                >
                    {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (isEdit ? 'Update Employee' : 'Create Employee')}
                </Button>
            </Box>
        </Box>
    );
}
